/**
 * useSceneSync Hook
 * Phase 2 Enhancement: Template Integration Fix
 * 
 * Synchronizes scene properties (script, layout, background) when scenes change
 * This is the missing piece that makes templates actually work!
 */

import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { currentSceneAtom } from '../atoms/sceneAtoms';

interface UseSceneSyncProps {
  onScriptChange?: (script: string) => void;
  onLayoutPresetChange?: (presetName: string, options?: { fromSceneLoad?: boolean }) => void;
  onBackgroundChange?: (background: any) => void;
  onLayoutChange?: (layout: any[]) => void;
  onSceneSwitch?: (scene: any) => void; // Called when scene ID changes (for stream requirements check)
  skipBackgroundSync?: boolean; // When true, skip background syncing (used in blank mode)
  skipLayoutSync?: boolean; // When true, skip layout syncing (used in blank mode)
  isRecording?: boolean; // Track recording state to prevent background sync during recording
}

/**
 * Hook that watches the current scene and triggers callbacks when it changes
 * This enables auto-loading scripts, layouts, and backgrounds from templates
 * 
 * IMPORTANT: Layout sync only triggers when switching to a DIFFERENT scene (scene ID changes)
 * This prevents unwanted layout resets when other scene properties change (e.g., status)
 */
export const useSceneSync = ({
  onScriptChange,
  onLayoutPresetChange,
  onBackgroundChange,
  onLayoutChange,
  onSceneSwitch,
  skipBackgroundSync = false,
  skipLayoutSync = false,
  isRecording = false,
}: UseSceneSyncProps = {}) => {
  const [currentScene] = useAtom(currentSceneAtom);
  
  // Track previous scene ID to detect actual scene switches vs. property updates
  const prevSceneIdRef = useRef<number | string | null>(null);
  // Track previous layoutPreset to detect preset changes within the same scene
  const prevLayoutPresetRef = useRef<string | null>(null);
  
  // Use refs to track state that should skip sync - refs update immediately without waiting for re-render
  const isRecordingRef = useRef(isRecording);
  const skipBackgroundSyncRef = useRef(skipBackgroundSync);
  
  // Update refs synchronously when props change
  isRecordingRef.current = isRecording;
  skipBackgroundSyncRef.current = skipBackgroundSync;

  // Sync script when scene changes (script can change within same scene via editing)
  // Also clear teleprompter when scene is unloaded (e.g., navigating to blank mode)
  useEffect(() => {
    if (onScriptChange) {
      onScriptChange(currentScene?.script || '');
    }
  }, [currentScene?.script, onScriptChange]);

  // Sync layout ONLY when scene ID changes (switching scenes) or layoutPreset changes
  // This prevents layout resets when other scene properties change (status, duration, etc.)
  useEffect(() => {
    if (skipLayoutSync) {
      prevSceneIdRef.current = null;
      prevLayoutPresetRef.current = null;
      return;
    }
    
    if (!currentScene) {
      prevSceneIdRef.current = null;
      prevLayoutPresetRef.current = null;
      return;
    }

    const sceneId = currentScene.id;
    const layoutPreset = currentScene.layoutPreset || null;
    const isSceneSwitch = prevSceneIdRef.current !== sceneId;
    const isPresetChange = prevLayoutPresetRef.current !== layoutPreset;
    
    // Update refs
    prevSceneIdRef.current = sceneId;
    prevLayoutPresetRef.current = layoutPreset;

    // Notify when scene switches (for stream requirements check, etc.)
    // This runs even when preset is the same
    if (isSceneSwitch && onSceneSwitch) {
      onSceneSwitch(currentScene);
    }

    // Only apply layout when switching to a different scene OR when preset explicitly changes
    if (!isSceneSwitch && !isPresetChange) {
      return;
    }

    // CRITICAL FIX: Prioritize saved layout over preset to prevent duplication
    // If scene has a saved layout array, use it directly (don't reapply preset)
    if (currentScene.layout && Array.isArray(currentScene.layout) && currentScene.layout.length > 0 && onLayoutChange) {
      onLayoutChange(currentScene.layout);
    } else if (currentScene.layoutPreset && onLayoutPresetChange) {
      // Pass fromSceneLoad: true to skip stream requirements check
      // This fixes the bug where presets weren't applied because streams weren't ready yet
      onLayoutPresetChange(currentScene.layoutPreset, { fromSceneLoad: true });
    }
  }, [currentScene?.id, currentScene?.layoutPreset, currentScene?.layout, onLayoutPresetChange, onLayoutChange, onSceneSwitch, skipLayoutSync]);

  // Sync background when scene changes (background can change within same scene via editing)
  // CRITICAL: Use refs to check skip conditions to avoid race conditions with React state updates
  useEffect(() => {
    // Check refs for the most up-to-date skip state - refs update synchronously
    const shouldSkip = skipBackgroundSyncRef.current || isRecordingRef.current;
    
    if (shouldSkip) {
      return;
    }
    
    if (!currentScene) {
      return;
    }

    if (currentScene.background && onBackgroundChange) {
      onBackgroundChange(currentScene.background);
    }
  }, [currentScene?.background, onBackgroundChange]);

  return {
    currentScene,
  };
};

export default useSceneSync;
